// Penyebab DP hangus MASIH MENAGIH pesanannya? — SATU definisi klien, cermin
// `hangus_dp_cause_bills_order` (mig 7100 §1, bughunt G7-12/G7-13).
//
// Nota DP yang belum dibayar saat tagihan barang pesanannya terbit di-void
// dengan `hangus_by_invoice_id` (mig 6590): nominal DP-nya "ikut ditagih" di
// tagihan barang itu. Klien dulu menganggap SETIAP nota DP void ber-penyebab
// sebagai "ditagih di tagihan barang X" tanpa melihat nasib X — padahal X bisa
// sudah DIBATALKAN, atau baris pesanan itu sudah dibatalkan dari X (gagal
// produksi, Edit Tagihan) sementara X tetap hidup karena pesanan lain.
// Akibatnya tombol "Terbitkan Nota DP" admin disembunyikan (jalur pemulihan
// mig 7030 tak terjangkau) dan pembeli disuruh membayar di tagihan yang sudah
// batal.
//
// Definisi server: ujung rantai gabung penyebab (ikuti `merged_into_invoice_id`
// sampai ujung, paling banyak 10 lompatan) berstatus open/partial/paid DAN
// masih memuat baris `product_item` AKTIF pesanan itu. Klien menilainya
// SENDIRI dari data yang sudah dimuat — tak menunggu data dibersihkan mig 7100
// (masih DITAHAN), jadi benar sebelum maupun sesudah 7100 di-apply.

/** Lompatan rantai gabung terjauh yang diikuti (cermin `t.depth < 10` server). */
export const HANGUS_CHAIN_MAX_HOPS = 10;

/** Status tagihan barang yang masih menagih (cermin §1 mig 7100). */
const BILLING_STATUSES: ReadonlySet<string> = new Set(["open", "partial", "paid"]);

/** Satu tagihan di rantai gabung penyebab. */
export type HangusCauseNode = {
  id: string;
  /** Status tagihan; null = tak termuat (rantainya tak bisa dinilai). */
  status: string | null;
  /** `merged_into_invoice_id`; null = ujung rantai. */
  mergedIntoId: string | null;
};

export type HangusOrphanReason =
  /** Ujung rantainya dibatalkan / ditolak. */
  | "batal"
  /**
   * Ujung rantainya tak lagi menagih pesanan itu: baris produknya dibatalkan
   * (gagal produksi, Edit Tagihan) atau statusnya bukan tagihan terbit.
   */
  | "tak_menagih";

export type HangusCauseVerdict<N extends HangusCauseNode = HangusCauseNode> =
  /** Ujung rantai hidup & masih memuat baris produk aktif pesanan itu. */
  | { kind: "bills"; end: N }
  /** DP "yatim": penyebabnya tak lagi menagih pesanan itu. */
  | { kind: "orphan"; end: N; reason: HangusOrphanReason }
  /**
   * Rantainya tak termuat utuh (simpul di tengah / ujung tak dikenal) —
   * pemanggil memilih sendiri arah amannya, biasanya tampilan lama.
   */
  | { kind: "unknown" };

/**
 * Nilai penyebab DP hangus sebuah pesanan.
 *
 * `nodes` = tagihan yang dikenal (penyebab + rantai gabungnya + tagihan barang
 * hidup lain yang termuat). `activeLineInvoiceIds` = tagihan yang memuat baris
 * `product_item` AKTIF pesanan itu; ABSEN = baris tak termuat sama sekali →
 * hanya status ujung rantai yang dinilai (kasus G7-13 tak terdeteksi, tapi
 * tagihan yang dibatalkan tetap terdeteksi).
 */
export function judgeHangusCause<N extends HangusCauseNode>(input: {
  causeId: string;
  nodes: ReadonlyMap<string, N>;
  activeLineInvoiceIds?: ReadonlySet<string>;
}): HangusCauseVerdict<N> {
  let node = input.nodes.get(input.causeId);
  let last = node;
  // Server mengikuti `merged_into` APA PUN statusnya sampai kedalaman 10, lalu
  // menilai simpul terdalam — simpul yang masih "digabung" di batas itu
  // berstatus void, jadi tak menagih.
  for (let hop = 0; node?.mergedIntoId && hop < HANGUS_CHAIN_MAX_HOPS; hop += 1) {
    last = node;
    node = input.nodes.get(node.mergedIntoId);
  }
  if (!node || node.status == null) {
    // Rantai tak termuat utuh. Tapi bila baris pesanan TERMUAT dan tak satu pun
    // tagihan memuat baris aktifnya, ujung mana pun pasti tak menagih.
    const known = node ?? last;
    if (known && input.activeLineInvoiceIds && input.activeLineInvoiceIds.size === 0) {
      return { kind: "orphan", end: known, reason: "tak_menagih" };
    }
    return { kind: "unknown" };
  }
  if (!BILLING_STATUSES.has(node.status)) {
    return {
      kind: "orphan",
      end: node,
      reason: node.status === "void" || node.status === "rejected" ? "batal" : "tak_menagih"
    };
  }
  if (input.activeLineInvoiceIds && !input.activeLineInvoiceIds.has(node.id)) {
    return { kind: "orphan", end: node, reason: "tak_menagih" };
  }
  return { kind: "bills", end: node };
}

// ─── Cara bayar DP yang ikut ditagih ─────────────────────────────────────────

/**
 * Cara bayar DP yang ikut ditagih di tagihan barang, menurut status tagihan
 * yang KINI menagihnya. Opsi "Bayar DP" hanya hidup selama tagihan itu BELUM
 * menerima uang (`bayarDpOption`; server `invoice_payment_kind_for`), jadi
 * kalimat "bayar lewat Bayar DP" salah begitu ada uang masuk, dan "bayar lewat
 * tagihan itu" salah begitu tagihannya lunas (bughunt 2026-10-13).
 *
 * Status cukup sebagai penanda uang: di PROD `open` ⇔ tanpa uang, `partial` /
 * `paid` ⇔ ada uang (cek 2026-10-13 atas 694 / 342 / 4.874 tagihan barang, nol
 * pengecualian). Status tak dikenal → "bayar_dp" (kalimat lama).
 */
export type BilledDpPay =
  /** Belum ada uang masuk — opsi Bayar DP tersedia. */
  | "bayar_dp"
  /** Sudah ada uang masuk — sisanya lewat Pembayaran / Pelunasan. */
  | "pelunasan"
  /** Tagihan barangnya lunas — DP-nya ikut terbayar. */
  | "lunas";

export function billedDpPay(goodsStatus: string | null | undefined): BilledDpPay {
  if (goodsStatus === "paid") return "lunas";
  if (goodsStatus === "partial") return "pelunasan";
  return "bayar_dp";
}

/**
 * Jalan bayar sisa tagihan barang yang SUDAH menerima uang — opsi Bayar DP tak
 * ada lagi di sana (dipakai juga kalimat kekurangan DP, bughunt G7-15).
 */
export function pelunasanPayHint(audience: "buyer" | "admin"): string {
  return audience === "admin"
    ? "catat lewat Catat Pembayaran → Pembayaran / Pelunasan, bukan Bayar DP"
    : "bayar lewat Bayar Sekarang → Pelunasan, bukan Bayar DP";
}

/**
 * Ekor kalimat kartu pesanan (keranjang admin & /orders pembeli) sesudah "DP
 * ditagih di tagihan barang X": cara bayarnya mengikuti keadaan tagihan itu.
 */
export function dpBilledCartTail(
  goodsStatus: string | null | undefined,
  audience: "buyer" | "admin"
): string {
  switch (billedDpPay(goodsStatus)) {
    case "lunas":
      return "sudah lunas bersama tagihan itu, tidak ada yang perlu dibayar lagi.";
    case "pelunasan":
      return `karena tagihan itu sudah menerima pembayaran, ${pelunasanPayHint(audience)}; tidak ditagih dua kali.`;
    default:
      return audience === "admin"
        ? "catat lewat Catat Pembayaran → Bayar DP di tagihan itu, tidak ditagih dua kali."
        : "bayar lewat Bayar Sekarang → Bayar DP, tidak ditagih dua kali.";
  }
}

// ─── Teks ────────────────────────────────────────────────────────────────────

/** "sudah dibatalkan" / "tak lagi menagih pesanan ini". */
export function hangusOrphanWhy(reason: HangusOrphanReason): string {
  return reason === "batal" ? "sudah dibatalkan" : "tak lagi menagih pesanan ini";
}

/**
 * Kalimat kartu pesanan (keranjang admin & /orders pembeli) untuk DP yang dulu
 * ikut ditagih di tagihan barang yang kini tak lagi menagihnya. TIDAK pernah
 * menyuruh membayar di tagihan itu (bughunt G7-12). Admin diarahkan ke tombol
 * "Terbitkan Nota DP" di kartu yang sama — server (mig 7030) membuka kembali
 * nota DP lamanya, atau memindahkannya ke tagihan barang hidup yang kini
 * menagih pesanan itu; tak pernah menerbitkan nota DP kedua.
 */
export function dpHangusOrphanCartNote(
  info: { goodsDisplayNo: string; reason: HangusOrphanReason },
  audience: "buyer" | "admin"
): string {
  const head = `DP pesanan ini dulu ikut ditagih di Tagihan Barang ${info.goodsDisplayNo}, yang kini ${hangusOrphanWhy(info.reason)}`;
  return audience === "admin"
    ? `${head} — sekarang tidak ditagih di mana pun. Terbitkan Tagihan DP untuk menagihnya lagi (tagihan DP lamanya yang dipakai, bukan tagihan baru).`
    : `${head} — jangan dibayar di sana. Tunggu kabar tagihan DP-nya dari admin.`;
}
